import {
  type QueryKey,
  useQuery,
  type UseQueryOptions,
  type UseQueryResult
} from '@tanstack/react-query'
import { useLocale } from 'next-intl'

interface CreateQueryHookConfig<TParams, TData, TError = Error> {
  getQueryKey: (params: TParams) => QueryKey
  queryFn: (params: TParams) => Promise<TData>
  /**
   * Set when the response text comes back translated — the client sends the UI
   * language as the `Lang` header (see api/client), so the same URL returns
   * different content per locale. The locale then belongs in the cache key:
   * without it, switching languages keeps serving the previous language's
   * response until the page is reloaded.
   */
  localized?: boolean
  getEnabled?: (params: TParams) => boolean
  getOptions?: (
    params: TParams
  ) => Omit<
    UseQueryOptions<TData, TError, TData, QueryKey>,
    'queryKey' | 'queryFn' | 'enabled'
  >
}

export function createQueryHook<TParams, TData, TError = Error>(
  config: CreateQueryHookConfig<TParams, TData, TError>
) {
  function useBaseQuery(
    params: TParams,
    keySuffix: QueryKey
  ): UseQueryResult<TData, TError> {
    return useQuery<TData, TError>({
      queryKey: [...config.getQueryKey(params), ...keySuffix],
      queryFn: () => config.queryFn(params),
      enabled: config.getEnabled ? config.getEnabled(params) : true,
      ...(config.getOptions ? config.getOptions(params) : {})
    })
  }

  // The locale read is picked once, here, rather than branched inside the
  // component: a plain query must not require an intl provider just to run.
  if (!config.localized) {
    return function useGeneratedQuery(params: TParams) {
      return useBaseQuery(params, [])
    }
  }

  return function useLocalizedQuery(params: TParams) {
    return useBaseQuery(params, [useLocale()])
  }
}
